import { useEffect, useMemo, useState } from 'react';
import { Loader2, Download, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Pre-download review checklist.
 * Persists confirmation in localStorage keyed by `${jobResultId}::${cvGeneratedAt || 'unknown'}`,
 * so a regenerated CV (new generated-at timestamp) requires a fresh review.
 */
const STORAGE_KEY = 'jobmatch.cv-review';

function loadAck(key) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const obj = JSON.parse(raw);
    return Boolean(obj && obj[key]);
  } catch { return false; }
}

function saveAck(key) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    obj[key] = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch { /* noop */ }
}

const CHECKS = [
  { id: 'read', label: 'I read the CV end-to-end.' },
  { id: 'company', label: 'I confirmed the company name and role title are correct.' },
  { id: 'metrics', label: 'I verified there are no fabricated metrics or claims.' },
];

export function ReviewBeforeDownloadModal({
  open,
  onClose,
  onConfirm,
  jobResultId,
  cvGeneratedAt,
  format,        // 'pdf' | 'docx'
  inProgress,
}) {
  const ackKey = useMemo(
    () => `${jobResultId}::${cvGeneratedAt || 'unknown'}`,
    [jobResultId, cvGeneratedAt]
  );

  const [checked, setChecked] = useState({});
  const allChecked = CHECKS.every((c) => checked[c.id]);

  // Reset state when opening for a fresh CV
  useEffect(() => {
    if (open) {
      const previouslyAcked = loadAck(ackKey);
      if (previouslyAcked) {
        setChecked(Object.fromEntries(CHECKS.map((c) => [c.id, true])));
      } else {
        setChecked({});
      }
    }
  }, [open, ackKey]);

  if (!open) return null;

  const handleConfirm = () => {
    saveAck(ackKey);
    onConfirm();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="cv-review-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cv-review-modal-heading"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-background border border-border rounded-xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h3 id="cv-review-modal-heading" className="font-serif-display text-xl font-medium">Review before downloading</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-5">
          Take 30 seconds to verify the CV is accurate before sending it out.
        </p>

        <ul className="space-y-3 mb-5">
          {CHECKS.map((c) => (
            <li key={c.id}>
              <label className="flex items-start gap-3 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(checked[c.id])}
                  onChange={(e) => setChecked((prev) => ({ ...prev, [c.id]: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-foreground"
                  data-testid={`cv-review-check-${c.id}`}
                />
                <span>{c.label}</span>
              </label>
            </li>
          ))}
        </ul>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={inProgress} className="btn-secondary text-sm h-auto px-4 py-2" data-testid="cv-review-back-btn">
            Back to edit
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!allChecked || inProgress}
            className="btn-primary text-sm h-auto px-4 py-2 disabled:opacity-50"
            data-testid="cv-review-download-btn"
          >
            {inProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            {inProgress ? 'Preparing…' : `Download ${(format || 'PDF').toUpperCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ReviewBeforeDownloadModal;
